import ApplicationController from '../../../../../javascript/controllers/application_controller'

const FOCUS_KEY = 'year-histogram-focus'
const KEYBOARD_COMMIT_DELAY_MS = 600

/**
 * Year Histogram Handles Controller
 *
 * Drives the two range handles under the histogram bars. Every value a handle
 * can write is rendered by the server; this controller only looks them up,
 * keeps the handles in order and submits `published_on`. An untouched handle
 * keeps the bound it was rendered with, so moving one end never rewrites the
 * other.
 *
 * Breadcrumbs:
 *   - Filter/year_range_applied: When user moves a range handle
 *
 * @extends ApplicationController
 */
export default class extends ApplicationController {
  static targets = ['start', 'end', 'value']

  static values = {
    startValues: Array,
    endValues: Array,
    yearValues: Array,
    valuetexts: Array,
    currentStart: String,
    currentEnd: String,
  }

  connect() {
    this.moved = new Set()
    this.keyboard = false
    // Turbo's snapshot may hold the field enabled from an earlier commit.
    this.valueTarget.disabled = true
    this.restoreFocus()
  }

  disconnect() {
    clearTimeout(this.commitTimer)
  }

  useKeyboard() {
    this.keyboard = true
  }

  usePointer() {
    this.keyboard = false
  }

  slide(event) {
    const handle = event.target
    this.moved.add(handle)

    handle.setAttribute('aria-valuetext', this.valuetextsValue[Number(handle.value)])
    this.startTarget.classList.toggle('z-10', handle === this.startTarget)
    this.endTarget.classList.toggle('z-10', handle === this.endTarget)
  }

  commit(event) {
    clearTimeout(this.commitTimer)
    const handle = event.target
    const delay = this.keyboard ? KEYBOARD_COMMIT_DELAY_MS : 0
    this.commitTimer = setTimeout(() => this.submit(handle), delay)
  }

  submit(handle) {
    const value = this.publishedValue()
    if (value === null) return

    this.valueTarget.value = value
    this.valueTarget.disabled = value === ''
    if (this.keyboard) this.rememberFocus(handle)
    this.addBreadcrumb('Filter', 'year_range_applied', { published_on: value })
    this.element.closest('form').requestSubmit()
  }

  /**
   * The handles may cross: whichever sits lower writes the start. An untouched
   * handle keeps its bound on whichever side it ends up, and a moved one looks
   * its side's value up for its slot.
   *
   * @returns {string|null} the `published_on` value; '' clears the filter, null changes nothing
   */
  publishedValue() {
    if (this.moved.size === 0) return null

    const [low, high] =
      this.startIndex <= this.endIndex
        ? [this.startTarget, this.endTarget]
        : [this.endTarget, this.startTarget]
    const lowIndex = Number(low.value)
    const highIndex = Number(high.value)

    const year = this.yearValuesValue[lowIndex]
    if (this.moved.size === 2 && lowIndex === highIndex && year) return year

    const start = this.moved.has(low) ? this.startValuesValue[lowIndex] : this.boundOf(low)
    const end = this.moved.has(high) ? this.endValuesValue[highIndex] : this.boundOf(high)
    if (start === '..' && end === '..') return ''

    return `${start}/${end}`
  }

  boundOf(handle) {
    return handle === this.startTarget ? this.currentStartValue : this.currentEndValue
  }

  get startIndex() {
    return Number(this.startTarget.value)
  }

  get endIndex() {
    return Number(this.endTarget.value)
  }

  rememberFocus(handle) {
    try {
      sessionStorage.setItem(FOCUS_KEY, handle.dataset.testid)
    } catch {
      // Blocked storage only costs the refocus after the reload.
    }
  }

  restoreFocus() {
    let testid
    try {
      testid = sessionStorage.getItem(FOCUS_KEY)
      sessionStorage.removeItem(FOCUS_KEY)
    } catch {
      return
    }
    if (!testid) return

    this.element.querySelector(`[data-testid="${testid}"]`)?.focus()
  }
}
